import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Bot, BrainCircuit, PencilLine, Settings2, Trash2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { AIModel } from '@/types';
import CollabFlowCanvas from './CollabFlowCanvas';
import ModelSelector from './ModelSelector';
import type { CollabConfig, CollabNodeConfig } from './collabTypes';
import {
  getCollabEditorNode,
  getCollabRoleDefinition,
  getCollabRoleDescription,
  getCollabRoleTitle,
  getDeepResearchNodes,
  getCollabDrafterNodes,
  getModelNameById,
  getNodeSettingsSelectable,
  normalizeCollabConfig,
  validateCollabConfig,
} from './collabTypes';

interface CollabConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: CollabConfig | null;
  models: AIModel[];
  preferredModelId?: string | null;
  onSave: (config: CollabConfig) => Promise<void> | void;
}

const getNewDrafterPosition = (draftConfig: CollabConfig) => {
  const drafters = getCollabDrafterNodes(draftConfig);
  const lastDrafter = drafters[drafters.length - 1];
  return {
    x: lastDrafter?.position.x ?? 290,
    y: (lastDrafter?.position.y ?? 90) + 120,
  };
};

export default function CollabConfigModal({
  open,
  onOpenChange,
  config,
  models,
  preferredModelId,
  onSave,
}: CollabConfigModalProps) {
  const [draftConfig, setDraftConfig] = useState<CollabConfig>(() => normalizeCollabConfig(config, models, preferredModelId));
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const normalized = normalizeCollabConfig(config, models, preferredModelId);
      const firstConfigurableNode = normalized.nodes.find((node) => getNodeSettingsSelectable(node.role));
      setDraftConfig(normalized);
      setSelectedNodeId(firstConfigurableNode?.id || null);
    }
  }, [open, config, models, preferredModelId]);

  const validation = useMemo(() => validateCollabConfig(draftConfig, models), [draftConfig, models]);
  const hasModels = models.length > 0;
  const drafterNodes = useMemo(() => getCollabDrafterNodes(draftConfig), [draftConfig]);
  const editorNode = useMemo(() => getCollabEditorNode(draftConfig), [draftConfig]);
  const researchNodes = useMemo(() => getDeepResearchNodes(draftConfig), [draftConfig]);
  const selectedNode = useMemo(
    () => draftConfig.nodes.find((node) => node.id === selectedNodeId) || null,
    [draftConfig.nodes, selectedNodeId]
  );
  const participantNodes = useMemo(
    () => draftConfig.workflow === 'deep_research'
      ? researchNodes
      : [...drafterNodes, ...(editorNode ? [editorNode] : [])],
    [draftConfig.workflow, drafterNodes, editorNode, researchNodes]
  );

  const updateNode = (nodeId: string, updates: Partial<CollabNodeConfig>) => {
    setDraftConfig((current) => ({
      ...current,
      nodes: current.nodes.map((node) => (node.id === nodeId ? { ...node, ...updates } : node)),
    }));
  };

  const handleAddDrafter = () => {
    const newNodeId = `drafter-${Date.now()}`;
    setDraftConfig((current) => ({
      ...current,
      nodes: [
        ...current.nodes.filter((node) => node.role !== 'output'),
        {
          id: newNodeId,
          role: 'drafter',
          modelId:
            models.find((model) => !drafterNodes.some((node) => node.modelId === model.id))?.id ||
            preferredModelId ||
            models[0]?.id ||
            null,
          position: getNewDrafterPosition(current),
        },
        ...current.nodes.filter((node) => node.role === 'output'),
      ],
    }));
    setSelectedNodeId(newNodeId);
  };

  const handleRemoveDrafter = (nodeId: string) => {
    setDraftConfig((current) => ({
      ...current,
      nodes: current.nodes.filter((node) => node.id !== nodeId),
    }));
    if (selectedNodeId === nodeId) {
      setSelectedNodeId(null);
    }
  };

  const handleSave = async () => {
    const normalized = normalizeCollabConfig(draftConfig, models, preferredModelId);
    const result = validateCollabConfig(normalized, models);
    if (!result.isValid) return;

    setIsSaving(true);
    try {
      await onSave(normalized);
      onOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  };

  const title = draftConfig.workflow === 'deep_research' ? 'Configure Deep Research' : 'Configure Collab';
  const description = draftConfig.workflow === 'deep_research'
    ? 'Assign one model per research role. The pipeline will plan, gather evidence, verify claims, assemble citations, and auto-publish the final report.'
    : 'Drafters create candidate answers. The editor combines and refines them into the final response.';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[96vw] max-w-6xl overflow-hidden p-0">
        <div className="flex h-full max-h-[92vh] flex-col">
          <DialogHeader className="border-b px-6 py-4">
            <div className="flex items-center gap-2">
              <DialogTitle>{title}</DialogTitle>
              <Badge variant="outline" className={draftConfig.workflow === 'deep_research' ? 'border-fuchsia-300 bg-fuchsia-50 text-fuchsia-700' : ''}>
                {draftConfig.workflow === 'deep_research' ? 'Deep Research' : 'Standard Collab'}
              </Badge>
            </div>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <div className="grid min-h-0 flex-1 gap-0 lg:grid-cols-[minmax(0,1fr)_340px]">
            <div className="min-h-0 p-4">
              <CollabFlowCanvas
                config={draftConfig}
                models={models}
                selectedNodeId={selectedNodeId}
                onSelectNode={setSelectedNodeId}
                onChange={setDraftConfig}
                onAddDrafter={handleAddDrafter}
              />
            </div>

            <div className="border-l bg-muted/20 p-4">
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold">Node settings</h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {draftConfig.workflow === 'deep_research'
                        ? 'Select a research role node to assign its model and inspect its responsibility.'
                        : 'Select a drafter or editor node to choose its model.'}
                    </p>
                  </div>
                  <Settings2 className="h-4 w-4 text-muted-foreground" />
                </div>

                {!hasModels ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>No models available</AlertTitle>
                    <AlertDescription>Add or enable models before saving a collab setup.</AlertDescription>
                  </Alert>
                ) : null}

                {selectedNode && getNodeSettingsSelectable(selectedNode.role) ? (
                  <div className="space-y-4 rounded-xl border bg-background p-4">
                    <div className="flex items-center gap-2">
                      {selectedNode.role === 'editor' ? (
                        <Bot className="h-4 w-4 text-violet-500" />
                      ) : draftConfig.workflow === 'deep_research' ? (
                        <BrainCircuit className="h-4 w-4 text-fuchsia-500" />
                      ) : (
                        <PencilLine className="h-4 w-4 text-sky-500" />
                      )}
                      <span className="text-sm font-medium">{getCollabRoleTitle(selectedNode.role)}</span>
                      {getCollabRoleDefinition(selectedNode.role).required ? <Badge variant="secondary">Required</Badge> : null}
                    </div>

                    <p className="text-xs text-muted-foreground">{getCollabRoleDescription(selectedNode.role)}</p>

                    <div className="space-y-2">
                      <label className="text-xs font-medium text-muted-foreground">Assigned model</label>
                      <ModelSelector
                        models={models}
                        selectedModelId={selectedNode.modelId || ''}
                        onModelChange={(modelId) => updateNode(selectedNode.id, { modelId })}
                        preferBrowser
                      />
                    </div>

                    {selectedNode.role === 'drafter' ? (
                      <Button type="button" variant="outline" size="sm" onClick={() => handleRemoveDrafter(selectedNode.id)}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Remove drafter
                      </Button>
                    ) : null}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed bg-background/70 p-4 text-sm text-muted-foreground">
                    {draftConfig.workflow === 'deep_research'
                      ? 'Click a research role node to assign its model and review its role in the pipeline.'
                      : 'Click a drafter or the editor node to adjust its model.'}
                  </div>
                )}

                <div className="space-y-3 rounded-xl border bg-background p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {draftConfig.workflow === 'deep_research' ? 'Research participants' : 'Current participants'}
                    </span>
                    <Badge variant="outline" className="rounded-full px-2.5 py-0.5">
                      {participantNodes.length} node{participantNodes.length === 1 ? '' : 's'}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    {participantNodes.map((node, index) => {
                      const roleTitle = draftConfig.workflow === 'deep_research'
                        ? getCollabRoleTitle(node.role)
                        : node.role === 'drafter'
                          ? `Drafter ${index + 1}`
                          : 'Editor';
                      const roleDescription = draftConfig.workflow === 'deep_research'
                        ? getCollabRoleDescription(node.role)
                        : node.role === 'drafter'
                          ? 'Candidate response generator'
                          : 'Final synthesis stage';

                      return (
                        <div
                          key={node.id}
                          className="rounded-lg border border-border/60 bg-background/60 px-3 py-3"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-medium">{roleTitle}</div>
                              <div className="mt-1 text-xs text-muted-foreground">{roleDescription}</div>
                            </div>
                            {getCollabRoleDefinition(node.role).required ? (
                              <Badge variant="secondary" className="shrink-0">
                                Required
                              </Badge>
                            ) : null}
                          </div>
                          <div className="mt-3 text-xs text-muted-foreground">
                            Model:&nbsp;
                            <span className="font-medium text-foreground">{getModelNameById(models, node.modelId)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {!validation.isValid ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Setup needs attention</AlertTitle>
                    <AlertDescription>
                      <ul className="ml-4 list-disc space-y-1">
                        {validation.errors.map((error) => (
                          <li key={error}>{error}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                ) : null}
              </div>
            </div>
          </div>

          <DialogFooter className="border-t px-6 py-4 sm:justify-between">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={!validation.isValid || !hasModels || isSaving}>
              {isSaving ? 'Saving…' : draftConfig.workflow === 'deep_research' ? 'Save Deep Research setup' : 'Save setup'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}